import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { Award, Brain, ChevronDown, HelpCircle, ListChecks, Megaphone, ShieldAlert } from "lucide-react";
import RulesService, { type Clause, type Rule, type TriggerEvent, type TriggerConfig } from "../../../_services/RulesService";
import PredicateBuilder from "./PredicateBuilder";
import ReviewerPicker from "./ReviewerPicker";
import DryRunPanel from "./DryRunPanel";

interface BadgeOption {
  Id: number;
  Name: string;
  AchievementType?: string;
}

interface Props {
  show: boolean;
  onHide: () => void;
  rule: Rule | null; // null = create mode
  badges: BadgeOption[];
  onSaved: (savedRuleId: number) => void;
}

const TRIGGER_OPTIONS: Array<{ value: TriggerEvent; label: string; desc: string }> = [
  { value: "resource_completion", label: "Resource completion", desc: "Fires when a student marks a study resource as done" },
  { value: "test_submission", label: "Test submission", desc: "Fires when a student's test score is posted" },
  { value: "attendance_update", label: "Attendance update", desc: "Fires when attendance for the student changes" },
  { value: "scheduled", label: "Daily schedule", desc: "Fires once per day via the scheduled-enqueue job (needed for time-based clauses)" },
  { value: "manual", label: "Manual re-evaluate", desc: "Allows admin Re-evaluate to trigger this rule" },
];

const RuleEditorModal: React.FC<Props> = ({ show, onHide, rule, badges, onSaved }) => {
  const isEdit = !!rule;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [badgeDefinitionId, setBadgeDefinitionId] = useState<number | "">("");
  const [predicate, setPredicate] = useState<Clause>({ type: "scope_completed", scopeType: "course", scopeId: 0 });
  const [events, setEvents] = useState<TriggerEvent[]>(["resource_completion"]);
  const [reviewRequired, setReviewRequired] = useState(false);
  const [reviewerUserIds, setReviewerUserIds] = useState<number[]>([]);
  const [notifyOnIssue, setNotifyOnIssue] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;
    if (rule) {
      setName(rule.name);
      setDescription(rule.description || "");
      setBadgeDefinitionId(rule.badgeDefinitionId);
      setPredicate(rule.predicate || { type: "scope_completed", scopeType: "course", scopeId: 0 });
      setEvents((rule.triggers?.events as TriggerEvent[]) || ["resource_completion"]);
      setReviewRequired(rule.reviewRequired);
      setReviewerUserIds(rule.reviewerUserIds || []);
      setNotifyOnIssue(rule.notifyOnIssue);
    } else {
      setName("");
      setDescription("");
      setBadgeDefinitionId("");
      setPredicate({ type: "scope_completed", scopeType: "course", scopeId: 0 });
      setEvents(["resource_completion"]);
      setReviewRequired(false);
      setReviewerUserIds([]);
      setNotifyOnIssue(true);
    }
    setErr(null);
  }, [show, rule]);

  const triggers: TriggerConfig = useMemo(() => ({ events, scopeFilter: null }), [events]);

  const toggleEvent = (e: TriggerEvent) => {
    setEvents((prev) => (prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]));
  };

  const save = async () => {
    if (!name.trim()) {
      setErr("Name is required.");
      return;
    }
    if (!badgeDefinitionId) {
      setErr("Pick a badge.");
      return;
    }
    if (events.length === 0) {
      setErr("Pick at least one trigger.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      if (isEdit && rule) {
        await RulesService.update(rule.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          predicate,
          triggers,
          reviewRequired,
          reviewerUserIds,
          notifyOnIssue,
        });
        onSaved(rule.id);
      } else {
        const res: any = await RulesService.create({
          badgeDefinitionId: Number(badgeDefinitionId),
          name: name.trim(),
          description: description.trim() || undefined,
          predicate,
          triggers,
          reviewRequired,
          reviewerUserIds,
          notifyOnIssue,
        });
        const newId = Number(res?.data?.data?.id || 0);
        onSaved(newId);
      }
    } catch (e: any) {
      setErr(e?.response?.data?.error?.message || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" dialogClassName="ca-badge-modal ca-rules-modal">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? "Edit Rule" : "New Issuance Rule"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="bm-grid">
          <div className="bm-form-col">
            {err && <div className="bm-error" style={{ marginBottom: 10 }}>{err}</div>}

            <HowItWorks />

            <Section icon={<ListChecks size={15} />} title="Identity" subtitle="Required">
              <Form.Group>
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Honour Roll — Final Exam ≥ 90"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What this rule represents — visible to other admins"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Badge *</Form.Label>
                <Form.Select
                  value={badgeDefinitionId}
                  onChange={(e) => setBadgeDefinitionId(e.target.value ? Number(e.target.value) : "")}
                  disabled={isEdit}
                >
                  <option value="">Select a badge…</option>
                  {badges.map((b) => (
                    <option key={b.Id} value={b.Id}>
                      {b.Name} {b.AchievementType ? `(${b.AchievementType})` : ""}
                    </option>
                  ))}
                </Form.Select>
                {isEdit && (
                  <Form.Text className="text-muted">
                    Badge can't be changed on an existing rule — create a new rule for a different badge.
                  </Form.Text>
                )}
              </Form.Group>
            </Section>

            <Section icon={<Brain size={15} />} title="Predicate">
              <Form.Text className="text-muted d-block mb-2">
                The student qualifies when this expression evaluates to true. Wrap clauses in AND / OR / NOT to compose.
              </Form.Text>
              <PredicateBuilder
                clause={predicate}
                onChange={setPredicate}
                isRoot
              />
            </Section>

            <Section icon={<Megaphone size={15} />} title="Triggers" subtitle="When to evaluate">
              <Form.Text className="text-muted d-block mb-2">
                Pick the event(s) that should trigger re-evaluation of this rule. Time-based clauses (enrollment age, login streak) need the <strong>Daily schedule</strong> trigger.
              </Form.Text>
              <div className="bm-trigger-grid">
                {TRIGGER_OPTIONS.map((t) => (
                  <label key={t.value} className={`bm-trigger-card ${events.includes(t.value) ? "is-active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={events.includes(t.value)}
                      onChange={() => toggleEvent(t.value)}
                    />
                    <span>
                      <span className="bm-trigger-card-title">{t.label}</span>
                      <span className="bm-trigger-card-sub">{t.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </Section>

            <Section icon={<ShieldAlert size={15} />} title="Mode" subtitle="Optional">
              <Form.Check
                type="switch"
                id="reviewRequiredSwitch"
                label="Require human approval before issuing (mark eligible, queue for review)"
                checked={reviewRequired}
                onChange={(e) => setReviewRequired(e.target.checked)}
              />
              {reviewRequired && (
                <Form.Group className="mt-3">
                  <Form.Label>Reviewers</Form.Label>
                  <ReviewerPicker value={reviewerUserIds} onChange={setReviewerUserIds} />
                </Form.Group>
              )}
              <Form.Check
                type="switch"
                id="notifyOnIssueSwitch"
                className="mt-3"
                label="Notify student on issuance (in-app + email)"
                checked={notifyOnIssue}
                onChange={(e) => setNotifyOnIssue(e.target.checked)}
              />
            </Section>
          </div>

          <div className="bm-preview-col">
            <div className="bm-preview-label">
              <Award size={12} /> Dry run
            </div>
            <div style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)", borderRadius: 12, padding: 16 }}>
              <p style={{ fontSize: 12, color: "var(--text-body)", marginBottom: 10 }}>
                Evaluate this rule against a specific student. No persistence, no issuance — just see the predicate trace.
              </p>
              <DryRunPanel ruleId={rule?.id ?? null} />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : isEdit ? "Update Rule" : "Create Rule"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const Section: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, children }) => (
  <div className="bm-section">
    <div className="bm-section-head">
      <span className="bm-section-head-icon">{icon}</span>
      <h4>{title}</h4>
      {subtitle && <span className="bm-section-sub">{subtitle}</span>}
    </div>
    <div className="bm-section-body">{children}</div>
  </div>
);

/**
 * Collapsible explainer that surfaces the three-section mental model right
 * where new admins first encounter it. Closed by default so repeat users
 * aren't taxed by it.
 */
const HowItWorks: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`bm-howitworks ${open ? "is-open" : ""}`}>
      <button
        type="button"
        className="bm-howitworks-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="bm-howitworks-toggle-left">
          <HelpCircle size={14} /> How rules work
        </span>
        <ChevronDown size={14} className="bm-howitworks-chevron" />
      </button>
      {open && (
        <div className="bm-howitworks-body">
          <p>
            A rule is three things, answering three different questions. The
            engine combines them every time a trigger fires.
          </p>
          <dl className="bm-howitworks-grid">
            <dt><Brain size={12} /> Predicate</dt>
            <dd>
              <strong>What</strong> must be true about the student. Pure condition
              — no notion of time. E.g.{" "}
              <code>attendance ≥ 95% AND test #7 score ≥ 80</code>.
            </dd>

            <dt><Megaphone size={12} /> Triggers</dt>
            <dd>
              <strong>When</strong> to evaluate. Pick the events that change the
              facts your predicate cares about. Time-only clauses (enrollment
              age, login streak) need <em>Daily schedule</em>.
            </dd>

            <dt><ShieldAlert size={12} /> Mode</dt>
            <dd>
              <strong>How</strong> a match is handled — auto-issue vs queue for
              human approval, and whether to notify the student.
            </dd>
          </dl>
          <div className="bm-howitworks-example">
            <strong>Example —</strong> "Issue Perfect Attendance to anyone with
            ≥95% in course&nbsp;#42":
            predicate = <code>attendance ≥ 95 in course #42</code>;
            triggers = <em>Attendance update</em> (live) +{" "}
            <em>Manual re-evaluate</em> (so admins can backfill); mode = auto-issue,
            notify on. Trigger fires → queued → evaluated within ~1 min →
            badge issued + student emailed.
          </div>
        </div>
      )}
    </div>
  );
};

export default RuleEditorModal;
